import type { ClientToSignal, SignalToClient } from './signalingProtocol';

const SIGNALING_URL = 'ws://localhost:3003';

export interface SignalingHandlers {
  onCreated?: (peerId: string) => void;
  onJoined?: (peerId: string, hostId: string, roomId: string) => void;
  onPeerJoined?: (peerId: string) => void;
  onPeerLeft?: (peerId: string) => void;
  onSignal?: (from: string, data: unknown) => void;
  onError?: (message: string) => void;
  onDepartures?: (tokens: string[]) => void;
  onRunClosed?: () => void;
}

export class SignalingClient {
  private ws: WebSocket;
  private ready: Promise<void>;

  constructor(private handlers: SignalingHandlers) {
    this.ws = new WebSocket(SIGNALING_URL);
    this.ready = new Promise((resolve, reject) => {
      this.ws.onopen = () => resolve();
      this.ws.onerror = () => reject(new Error('signaling connection failed'));
    });
    this.ws.onmessage = (e) => {
      const msg = JSON.parse(e.data as string) as SignalToClient;
      switch (msg.type) {
        case 'created':
          this.handlers.onCreated?.(msg.peerId);
          break;
        case 'joined':
          this.handlers.onJoined?.(msg.peerId, msg.hostId, msg.roomId);
          break;
        case 'peer-joined':
          this.handlers.onPeerJoined?.(msg.peerId);
          break;
        case 'peer-left':
          this.handlers.onPeerLeft?.(msg.peerId);
          break;
        case 'signal':
          this.handlers.onSignal?.(msg.from, msg.data);
          break;
        case 'error':
          this.handlers.onError?.(msg.message);
          break;
        case 'run-departures':
          this.handlers.onDepartures?.(msg.tokens);
          break;
        case 'run-closed':
          this.handlers.onRunClosed?.();
          break;
      }
    };
  }

  private async sendMsg(msg: ClientToSignal): Promise<void> {
    await this.ready;
    this.ws.send(JSON.stringify(msg));
  }
  create(code: string, roomId: string): Promise<void> {
    return this.sendMsg({ type: 'create', code, roomId });
  }
  join(code: string): Promise<void> {
    return this.sendMsg({ type: 'join', code });
  }
  signal(to: string, data: unknown): Promise<void> {
    return this.sendMsg({ type: 'signal', to, data });
  }
  leaveRun(code: string, token: string): Promise<void> {
    return this.sendMsg({ type: 'leave-run', code, token });
  }
  /** Fire-and-forget on the live socket: the caller (host leave) closes right
   *  after, so this must flush synchronously rather than await the open promise. */
  endRun(code: string): void {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'end-run', code }));
    }
  }
  close(): void {
    this.ws.close();
  }
}
